"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VermontRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const unwrap_1 = require("../../unwrap");
const _stateRules_1 = require("./_stateRules");
const VT_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste", "operatesUnderSubpartP"],
        label: "Governing program",
        citation: "40 CFR 273.9, 261.6(a)(4), 266.501",
        description: (0, unwrap_1.unwrap) `Which program governs a container is answered the federal way. Open for
      confirmation: Vermont's universal waste (subchapter 9), used oil and pharmaceutical provisions
      were not read for this table, so the kind-program rows claim the federal answers.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "RCRA",
            },
            { when: { wasteKind: "USED_OIL" }, produces: "USED_OIL" },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: true },
                produces: "PHARMACEUTICAL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: false },
                produces: "RCRA",
            },
            { when: { wasteKind: "RCRA" }, produces: "RCRA" },
            { when: { wasteKind: null }, produces: "RCRA" },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
const VT_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "VHWMR § 7-310(a)",
    jurisdiction: "VT",
};
const VT_NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "VHWMR § 7-310(a)",
    jurisdiction: "VT",
};
const VT_VSQG_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "VHWMR § 7-306",
    jurisdiction: "VT",
};
const FEDERAL_UNIVERSAL_WASTE_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 273.15(a), 273.35(a)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_SUBPART_P_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 266.502(f)(1)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_USED_OIL_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 279.22",
    jurisdiction: "FEDERAL",
};
const VT_EXPECTATIONS = [
    // ── SHORT-TERM STORAGE (CENTRAL) AREAS ─────────────────────────────────
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "LQG" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "VHWMR § 7-308(b)(2)", jurisdiction: "VT" },
        },
        note: (0, unwrap_1.unwrap) `
      KNOWN GAPS

      1. § 7-308(b)(2) allows 180 days for F006 electroplating sludge managed under 40 CFR
      262.17(c)-(e); not supported, so F006 is held to 90 days (over-strict, same as federal).

      2. Shift accumulation (§ 7-310(b)) is not supported. Its accumulation containers sit in the
      short-term storage area undated until they hold 55 gallons, 1 quart of acute waste or one cubic
      yard of non-liquid Vermont-listed waste, or fill; the clock here runs from first fill
      (over-strict).
    `,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG" },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "VHWMR § 7-307(c)(2)", jurisdiction: "VT" },
        },
        note: (0, unwrap_1.unwrap) `
      No "must transport 200 miles" exception in Vermont.

      KNOWN GAP: shift accumulation (§ 7-310(b)) is not supported; see LQG.
    `,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "VSQG" },
        expect: { clock: VT_VSQG_NO_LIMIT },
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: null },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — § 7-310(a) ────────────────────────────────
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: "LQG" },
        expect: { caps: VT_SATELLITE_CAPS, clock: VT_NO_STANDING_SATELLITE_CLOCK },
        note: (0, unwrap_1.unwrap) `
      § 7-310(a) caps a satellite area at 55 gallons, 1 quart of liquid acute or 1 kg of solid acute
      waste (standard federal caps).
      
      KNOWN GAPS
      
      1. VT separately allows one cubic yard of NON-LIQUID Vermont-listed waste; this is not supported.
      Non-liquid Vermont-listed waste is held to the 55 gallons (over-strict since 1 cubic yard is
      ~200 gallons, never under-strict). 
      
      2. § 7-310(a) also starts the 3-day move window when
      a smaller container becomes full; the move window here starts only when the area exceeds the caps
      (under-strict if you are using a container with capacity <55 gallons). 
      
      3. Transfers between satellite containers are prohibited in Vermont per DEC guidance (2023
      "Accumulation and Storage" fact sheet; not in the VHWMR text). Encamp does not prohibit them.
    `,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: "SQG" },
        expect: { caps: VT_SATELLITE_CAPS, clock: VT_NO_STANDING_SATELLITE_CLOCK },
        note: `Same as LQG.`,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: "VSQG" },
        expect: { caps: VT_VSQG_NO_LIMIT, clock: VT_NO_STANDING_SATELLITE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: null },
        expect: { caps: VT_SATELLITE_CAPS, clock: VT_NO_STANDING_SATELLITE_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
    // ── KIND PROGRAMS — federal, pending confirmation ──────────────────────
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_UNIVERSAL_WASTE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_UNIVERSAL_WASTE_CLOCK,
        },
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_USED_OIL_NO_LIMIT },
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_USED_OIL_NO_LIMIT,
        },
    },
    {
        given: { effectiveWasteKind: "PHARMACEUTICAL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_SUBPART_P_CLOCK },
    },
    {
        given: { effectiveWasteKind: "PHARMACEUTICAL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_SUBPART_P_CLOCK,
        },
    },
];
/**
 * VERMONT ACCUMULATION THRESHOLD EXPECTATIONS — VHWMR §§ 7-306(a), 7-307(a)
 *
 * The federal figures; the inherited slot answers them. Authored rather than
 * inherited so the SME packet prints claims someone read for Vermont.
 */
const VT_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
        note: (0, unwrap_1.unwrap) `§ 7-306(a) words the limit as "has accumulated less than"
      these figures, where federal 262.14 reads "never accumulates more than". 
      We use the federal reading, meaning we allow hitting the threshold exactly without considering it
      going over. This is technically infinitesimally less strict than Vermont regulations actually say.`,
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
    },
];
const VT_SOURCES = [
    {
        title: "Vermont Hazardous Waste Management Regulations, effective July 10, 2026",
        url: "https://dec.vermont.gov/sites/dec/files/wmp/HazWaste/Documents/Regulations/VHWMR%2C%20Effective%2007102026.pdf",
    },
    {
        title: "Accumulation and Storage of Hazardous Waste fact sheet, 2023 (Vermont DEC)",
        url: "https://dec.vermont.gov/sites/dec/files/wmp/HazWaste/Documents/FactSheets/Accumulation%20and%20Storage%20of%20HW%20Fact%20Sheet%202023.pdf",
    },
];
/** Vermont (ANR DEC) accumulation limits, VHWMR §§ 7-306 to 7-311. */
class VermontRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.expectations = VT_EXPECTATIONS;
        this.accumulationThresholdExpectations = VT_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = VT_DERIVATIONS;
        this.sources = VT_SOURCES;
        this.stateStatusDomain = [];
    }
    centralClock(ctx) {
        if (this.effectiveWasteKind(ctx) !== "RCRA") {
            return super.centralClock(ctx);
        }
        if (ctx.generatorStatus === "LQG") {
            return { kind: "TIME", days: 90, citation: "VHWMR § 7-308(b)(2)", jurisdiction: "VT" };
        }
        if (ctx.generatorStatus === "SQG") {
            // No 270-day option, so mustTransport200Miles is not read.
            return { kind: "TIME", days: 180, citation: "VHWMR § 7-307(c)(2)", jurisdiction: "VT" };
        }
        if (ctx.generatorStatus === "VSQG") {
            return VT_VSQG_NO_LIMIT;
        }
        // The federal UNDETERMINED: its payload names the same missing axis.
        return super.centralClock(ctx);
    }
    satelliteCaps(ctx) {
        if (this.effectiveWasteKind(ctx) !== "RCRA") {
            return super.satelliteCaps(ctx);
        }
        if (ctx.generatorStatus === "VSQG") {
            return VT_VSQG_NO_LIMIT;
        }
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "VHWMR § 7-310(a)",
            jurisdiction: "VT",
        };
    }
    satelliteClock(ctx) {
        if (this.effectiveWasteKind(ctx) !== "RCRA") {
            return super.satelliteClock(ctx);
        }
        return VT_NO_STANDING_SATELLITE_CLOCK;
    }
}
exports.VermontRules = VermontRules;
//# sourceMappingURL=VT.js.map